import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";

export const ANNOUNCE_SETTLE_MS = 100;
export const ANNOUNCE_CLEAR_MS = 8000;

const AnnouncerContext = createContext(null);

const POLITENESS = ["polite", "assertive"];

const createChannel = (node) => ({
  node,
  pending: [],
  settleTimer: null,
  clearTimer: null,
});

function writeChannel(channel, message) {
  channel.pending.push(message);
  if (channel.settleTimer) return;
  clearTimeout(channel.clearTimer);
  channel.clearTimer = null;
  if (channel.node) channel.node.textContent = "";
  channel.settleTimer = setTimeout(() => {
    channel.settleTimer = null;
    const text = channel.pending.join(" ");
    channel.pending = [];
    if (!channel.node) return;
    channel.node.textContent = text;
    channel.clearTimer = setTimeout(() => {
      channel.clearTimer = null;
      if (channel.node) channel.node.textContent = "";
    }, ANNOUNCE_CLEAR_MS);
  }, ANNOUNCE_SETTLE_MS);
}

function closeChannel(channel) {
  clearTimeout(channel.settleTimer);
  clearTimeout(channel.clearTimer);
  channel.settleTimer = null;
  channel.clearTimer = null;
  channel.node = null;
  const pending = channel.pending;
  channel.pending = [];
  return pending;
}

export function LiveRegions({
  root = false,
  visiblePolite = false,
  className,
}) {
  const context = useContext(AnnouncerContext);
  const politeRef = useRef(null);
  const assertiveRef = useRef(null);

  useLayoutEffect(() => {
    if (!context) return undefined;
    return context.register({
      root,
      polite: createChannel(politeRef.current),
      assertive: createChannel(assertiveRef.current),
    });
  }, [context, root]);

  return (
    <>
      <div
        ref={politeRef}
        className={visiblePolite ? className : "visually-hidden"}
        aria-live="polite"
        aria-atomic="true"
      />
      <div
        ref={assertiveRef}
        className="visually-hidden"
        aria-live="assertive"
        aria-atomic="true"
      />
    </>
  );
}

export function LiveAnnouncerProvider({ children }) {
  const outletsRef = useRef([]);

  const register = useCallback((outlet) => {
    const outlets = outletsRef.current;
    if (outlet.root) outlets.unshift(outlet);
    else outlets.push(outlet);
    return () => {
      const index = outlets.indexOf(outlet);
      if (index !== -1) outlets.splice(index, 1);
      const top = outlets[outlets.length - 1];
      POLITENESS.forEach((politeness) => {
        const pending = closeChannel(outlet[politeness]);
        if (top) {
          pending.forEach((message) => writeChannel(top[politeness], message));
        }
      });
    };
  }, []);

  const announce = useCallback((message, { politeness = "polite" } = {}) => {
    if (!message) return;
    const outlets = outletsRef.current;
    const top = outlets[outlets.length - 1];
    if (!top) return;
    writeChannel(
      politeness === "assertive" ? top.assertive : top.polite,
      String(message),
    );
  }, []);

  const isDialogOutletOnTop = useCallback(() => {
    const outlets = outletsRef.current;
    const top = outlets[outlets.length - 1];
    return !!top && !top.root;
  }, []);

  const value = useMemo(
    () => ({ announce, register, isDialogOutletOnTop }),
    [announce, register, isDialogOutletOnTop],
  );

  return (
    <AnnouncerContext.Provider value={value}>
      {children}
      <LiveRegions root />
    </AnnouncerContext.Provider>
  );
}

const noop = () => {};

export function useAnnounce() {
  return useContext(AnnouncerContext)?.announce ?? noop;
}

const never = () => false;

export function useIsDialogOutletOnTop() {
  return useContext(AnnouncerContext)?.isDialogOutletOnTop ?? never;
}
